import { sampleMetrics } from './sample-company';
import { addDays, madridDay, madridLocalToIso, MADRID_TIME_ZONE } from 'src/app/utils/madrid-day';
import type {
  CorporateRideDetailApi,
  CorporateRideKpisApi,
  CorporateRidePeriod,
  CorporateRideStateGroup,
} from 'src/app/models/corporation-ride.interface';
import type {
  RideReviewDecision,
  RideReviewDetailApi,
  RideReviewKpisApi,
  RideReviewLevel,
  RideReviewSignal,
} from 'src/app/models/corporate-ride-review.interface';
import type {
  RideBookingApi,
  RideBookingCategory,
  RideBookingReceiptApi,
  RideBookingRulesApi,
  RideBookingStatus,
  RideBookingVehicleDetailApi,
} from 'src/app/models/corporate-ride-booking.interface';
import type { RideGroupApi, RideGroupEmployeeApi } from 'src/app/models/ride-policy.interface';

/**
 * Taxi / VTC de «WAIIS S.L.» para el dashboard de ejemplo (#20261103-104013):
 * los viajes que paga la empresa («Viajes y gasto»), su revisión («Revisión»)
 * y las reservas de grupo («Reservas» y los marcadores de Inicio).
 *
 * Todo sale de la misma plantilla que el resto del ejemplo: quien viaja es un
 * empleado de `assets/demo/employees.json` (vinculado y activo), en su sede
 * (Madrid o Getafe, las de `assets/demo/offices.json`), y sale de casa de las
 * rutas de ejemplo del mapa o va a clientes, estaciones y el aeropuerto de su
 * zona. Las fechas cuentan hacia atrás (y las reservas, hacia delante) desde
 * el día de Madrid en que se mira, así que el ejemplo nunca se ve viejo; los
 * importes y las cifras de las pantallas se calculan con las reglas del back
 * a partir de estas mismas filas, para que cuadren entre sí.
 */

const CORPORATION_ID = 'ejemplo';
const MADRID = 'demo-office-madrid';
const GETAFE = 'demo-office-getafe';

/** Programa de Taxi / VTC de la empresa: días hacia atrás con viajes (hoy incluido). */
export const SAMPLE_RIDE_HISTORY_DAYS = 180;
/** Tope por viaje del grupo predeterminado: lo que pasa de ahí lo paga el empleado. */
export const SAMPLE_RIDE_LIMIT_CENTS = 4000;
/** Comisión de WAIIS sobre el precio del proveedor (viajes y reservas). */
export const SAMPLE_RIDE_COMMISSION_PERCENT = 10;
/** Tarifa fija del taxi al aeropuerto de Madrid. */
const AIRPORT_FLAT_CENTS = 3300;

interface Place {
  address: string;
  lat: number;
  lng: number;
}

const OFFICES: Record<string, Place & { name: string }> = {
  [MADRID]: { name: 'Sede central · Madrid', address: 'Paseo de la Castellana, 95, Madrid', lat: 40.4479, lng: -3.6925 },
  [GETAFE]: { name: 'Centro logístico · Getafe', address: 'Calle de la Confianza, 4, Getafe', lat: 40.3057, lng: -3.7329 },
};

export interface SampleTaxiRider {
  /** Número de `demo-user-N` en `assets/demo/employees.json`. */
  n: number;
  name: string;
  surname: string;
  department: string;
  officeId: string;
  /** Su casa: el barrio de su ruta de ejemplo del mapa. */
  home: Place;
}

/** Empleados vinculados y activos que piden taxi o VTC con cargo a la empresa. */
export const SAMPLE_TAXI_RIDERS: SampleTaxiRider[] = [
  { n: 1, name: 'Lucía', surname: 'García Martín', department: 'Logística', officeId: MADRID, home: { address: 'Calle de Santa Engracia, 120, Madrid', lat: 40.434, lng: -3.704 } },
  { n: 3, name: 'Martina', surname: 'Sánchez Ruiz', department: 'Ventas', officeId: MADRID, home: { address: 'Avenida de Europa, 14, Pozuelo de Alarcón', lat: 40.435, lng: -3.814 } },
  { n: 5, name: 'Sofía', surname: 'Moreno Álvarez', department: 'Atención al cliente', officeId: MADRID, home: { address: 'Calle de Silvano, 75, Madrid', lat: 40.472, lng: -3.642 } },
  { n: 7, name: 'Valeria', surname: 'Navarro Torres', department: 'Administración', officeId: MADRID, home: { address: 'Calle de Goya, 85, Madrid', lat: 40.429, lng: -3.677 } },
  { n: 11, name: 'Carmen', surname: 'Ruiz Ortega', department: 'Recursos humanos', officeId: MADRID, home: { address: 'Calle de la Princesa, 60, Madrid', lat: 40.43, lng: -3.718 } },
  { n: 12, name: 'Javier', surname: 'Molina Castro', department: 'Finanzas', officeId: MADRID, home: { address: 'Avenida de España, 20, Alcobendas', lat: 40.537, lng: -3.64 } },
  { n: 13, name: 'Elena', surname: 'Ortiz Delgado', department: 'Marketing', officeId: MADRID, home: { address: 'Avenida de Colmenar Viejo, 8, Tres Cantos', lat: 40.601, lng: -3.708 } },
  { n: 16, name: 'Diego', surname: 'Medina Garrido', department: 'Sistemas', officeId: MADRID, home: { address: 'Avenida de la Albufera, 150, Madrid', lat: 40.392, lng: -3.66 } },
  { n: 2, name: 'Hugo', surname: 'Fernández López', department: 'Administración', officeId: GETAFE, home: { address: 'Avenida de Fuenlabrada, 40, Leganés', lat: 40.328, lng: -3.764 } },
  { n: 6, name: 'Daniel', surname: 'Jiménez Romero', department: 'Logística', officeId: GETAFE, home: { address: 'Calle Real, 30, Parla', lat: 40.238, lng: -3.768 } },
  { n: 8, name: 'Álvaro', surname: 'Domínguez Gil', department: 'Ventas', officeId: GETAFE, home: { address: 'Calle de Leganés, 25, Fuenlabrada', lat: 40.284, lng: -3.794 } },
  { n: 10, name: 'Adrián', surname: 'Serrano Blanco', department: 'Atención al cliente', officeId: GETAFE, home: { address: 'Calle Mayor, 40, Alcorcón', lat: 40.348, lng: -3.824 } },
  { n: 21, name: 'Andrea', surname: 'Calvo Vidal', department: 'Almacén', officeId: GETAFE, home: { address: 'Calle de la Hiruela, 3, Pinto', lat: 40.242, lng: -3.699 } },
];

/** Otros empleados de la plantilla que van en las reservas de grupo (vinculados). */
const BOOKING_PASSENGERS: Record<string, number[]> = {
  [MADRID]: [1, 3, 5, 7, 11, 12, 13, 14, 15, 16, 17],
  [GETAFE]: [2, 6, 8, 10, 21],
};

const CLIENTS: Record<string, Place[]> = {
  [MADRID]: [
    { address: 'Calle de Alcalá, 265, Madrid', lat: 40.4318, lng: -3.6544 },
    { address: 'Paseo de la Castellana, 259, Madrid', lat: 40.4766, lng: -3.6872 },
    { address: 'Calle de Serrano, 41, Madrid', lat: 40.43, lng: -3.6874 },
    { address: 'Avenida de América, 32, Madrid', lat: 40.439, lng: -3.676 },
  ],
  [GETAFE]: [
    { address: 'Polígono Industrial Los Olivos, Getafe', lat: 40.3135, lng: -3.717 },
    { address: 'Calle Rey Pastor, 12, Leganés', lat: 40.3418, lng: -3.762 },
    { address: 'Parque Empresarial Carpetania, Getafe', lat: 40.2909, lng: -3.7036 },
  ],
};

const AIRPORT: Place = { address: 'Aeropuerto Adolfo Suárez Madrid-Barajas, T4', lat: 40.4919, lng: -3.5938 };
const ATOCHA: Place = { address: 'Estación de Puerta de Atocha, Madrid', lat: 40.4066, lng: -3.6892 };
const CHAMARTIN: Place = { address: 'Estación de Chamartín, Madrid', lat: 40.4722, lng: -3.6823 };
const IFEMA: Place = { address: 'IFEMA, Avenida del Partenón, 5, Madrid', lat: 40.4636, lng: -3.6163 };

const PROVIDERS = [
  { provider: 'uber', display: 'Uber', product: 'Comfort', factor: 1 },
  { provider: 'cabify', display: 'Cabify', product: 'Lite', factor: 1.08 },
  { provider: 'bolt', display: 'Bolt', product: 'Economy', factor: 0.92 },
];

// ─── Utilidades ──────────────────────────────────────────────────────────────

/** Pseudoaleatorio con semilla (mulberry32): el mismo día siempre da los mismos viajes. */
function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Km por carretera entre dos puntos (línea recta × 1,35). */
function roadKm(a: Place, b: Place): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h)) * 1.35;
}

/** Hora de Madrid `HH:MM` de un día `Y-m-d` → ISO con su desfase. */
function madridIso(day: string, time: string): string {
  return madridLocalToIso(`${day}T${time}`)!;
}

/** Un instante → ISO con la hora de Madrid y su desfase (los 10 primeros caracteres son el día de Madrid). */
function madridIsoOf(date: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: MADRID_TIME_ZONE, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(date);
  return madridIso(madridDay(date), parts);
}

/** Suma minutos a un ISO de Madrid y lo devuelve también en hora de Madrid. */
function plusMinutes(iso: string, minutes: number): string {
  return madridIsoOf(new Date(Date.parse(iso) + minutes * 60_000));
}

function hhmm(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

/** Día de la semana (0 domingo … 6 sábado) de un día `Y-m-d`. */
function weekday(day: string): number {
  return new Date(`${day}T00:00:00Z`).getUTCDay();
}

const sum = (values: number[]) => values.reduce((a, b) => a + b, 0);

function riderByN(n: number): SampleTaxiRider {
  return SAMPLE_TAXI_RIDERS.find((r) => r.n === n)!;
}

// ─── Viajes y gasto ──────────────────────────────────────────────────────────

/** Lo que distingue a un viaje de la bandeja de revisión. */
interface ReviewSpec {
  level: RideReviewLevel;
  decision: RideReviewDecision;
  signals: RideReviewSignal[];
  hardRules?: { code: string; text: string }[];
  /** Quién decidió (empleados de la plantilla con acceso al panel) y cuándo, en minutos tras el viaje. */
  decidedAfterMinutes?: number;
  rejectReason?: 'personal_trip';
  rejectMessage?: string;
}

interface RideSpec {
  id: string;
  rider: SampleTaxiRider;
  tripAt: string;
  reason: string;
  from: Place;
  to: Place;
  providerIndex: number;
  state: 'completed' | 'cancelled_by_rider' | 'in_progress';
  awaitingReceipt?: boolean;
  cancelFeeCents?: number;
  justification?: string | null;
  review?: ReviewSpec;
}

const SIGNAL_TEXT: Record<string, string> = {
  weekend: 'Viaje en fin de semana',
  night: 'Horario de madrugada',
  unrelated_origin: 'Origen sin relación con una sede, una estación o un lugar conocido',
  unrelated_destination: 'Destino sin relación con una sede, una estación o un lugar conocido',
  cost_above_average: 'Coste superior a la media del empleado',
  duplicate: 'Posible duplicidad con otro viaje casi idéntico',
  missing_justification: 'Sin justificación en un viaje atípico',
};

const signal = (code: string, points: number): RideReviewSignal => ({ code, points, text: SIGNAL_TEXT[code] });

/** Quien decide en la revisión: Carmen, de Recursos humanos. */
const REVIEWER = riderByN(11);

/** Viajes generados de un día laborable: entre 1 y 4, algo más en los últimos meses. */
function generatedRides(day: string, daysAgo: number): RideSpec[] {
  const wd = weekday(day);
  if (wd === 0 || wd === 6) return [];
  const rnd = seeded(daysAgo * 7919 + 17);
  const growth = daysAgo < 60 ? 1 : daysAgo < 120 ? 0.5 : 0;
  const count = 1 + Math.floor(rnd() * 3 + growth);
  const rides: RideSpec[] = [];
  for (let k = 0; k < count; k++) {
    const rider = SAMPLE_TAXI_RIDERS[Math.floor(rnd() * SAMPLE_TAXI_RIDERS.length)];
    const office = OFFICES[rider.officeId];
    const other = OFFICES[rider.officeId === MADRID ? GETAFE : MADRID];
    const pick = rnd();
    let reason: string;
    let from: Place = office;
    let to: Place;
    let minutes: number;
    if (pick < 0.42) {
      reason = 'client_visit';
      const clients = CLIENTS[rider.officeId];
      to = clients[Math.floor(rnd() * clients.length)];
      minutes = 9 * 60 + 30 + Math.floor(rnd() * 7 * 60);
    } else if (pick < 0.67) {
      reason = 'late_exit';
      to = rider.home;
      minutes = 20 * 60 + 45 + Math.floor(rnd() * 90);
    } else if (pick < 0.82) {
      reason = 'between_offices';
      to = other;
      minutes = 8 * 60 + 15 + Math.floor(rnd() * 8 * 60);
    } else {
      reason = 'other';
      to = [AIRPORT, ATOCHA, CHAMARTIN][Math.floor(rnd() * 3)];
      minutes = 7 * 60 + Math.floor(rnd() * 11 * 60);
      // A veces vuelven del viaje de trabajo a la sede.
      if (rnd() < 0.3) [from, to] = [to, office];
    }
    minutes -= minutes % 5;
    const cancelled = rnd() < 0.05;
    rides.push({
      id: `demo-ride-${day}-${k + 1}`,
      rider,
      tripAt: madridIso(day, hhmm(minutes)),
      reason,
      from,
      to,
      providerIndex: Math.floor(rnd() * PROVIDERS.length),
      state: cancelled ? 'cancelled_by_rider' : 'completed',
      cancelFeeCents: cancelled ? (rnd() < 0.5 ? 0 : 350) : undefined,
      justification: null,
    });
  }
  return rides;
}

/** Día laborable `daysAgo` días atrás (o el anterior), para los viajes que se cuentan a mano. */
function weekdayBefore(today: string, daysAgo: number): string {
  let day = addDays(today, -daysAgo);
  while (weekday(day) === 0 || weekday(day) === 6) day = addDays(day, -1);
  return day;
}

/** El último sábado que ya pasó (como poco, anteayer). */
function lastSaturday(today: string, minDaysAgo: number): string {
  let day = addDays(today, -minDaysAgo);
  while (weekday(day) !== 6) day = addDays(day, -1);
  return day;
}

/**
 * Los viajes que cuentan una historia en la revisión: dos de revisión
 * obligatoria y dos recomendada que esperan a la empresa, y tres ya decididos.
 */
function curatedRides(today: string): RideSpec[] {
  const madridOffice = OFFICES[MADRID];
  const getafeOffice = OFFICES[GETAFE];
  const javier = riderByN(12);
  const alvaro = riderByN(8);
  const elena = riderByN(13);
  const daniel = riderByN(6);
  const martina = riderByN(3);
  const hugo = riderByN(2);
  const diego = riderByN(16);
  const saturday = lastSaturday(today, 2);
  const duplicateDay = weekdayBefore(today, 3);
  return [
    {
      id: `demo-ride-${saturday}-weekend`,
      rider: javier,
      tripAt: madridIso(saturday, '23:40'),
      reason: 'other',
      from: { address: 'Calle de Ponzano, 12, Madrid', lat: 40.4405, lng: -3.7012 },
      to: javier.home,
      providerIndex: 0,
      state: 'completed',
      justification: null,
      review: {
        level: 'red',
        decision: 'pending',
        signals: [signal('weekend', 30), signal('night', 25), signal('unrelated_origin', 25)],
        hardRules: [{ code: 'off_hours_unrelated_trip', text: 'Fin de semana o madrugada con origen y destino no relacionados' }],
      },
    },
    {
      id: `demo-ride-${duplicateDay}-duplicate`,
      rider: alvaro,
      tripAt: madridIso(duplicateDay, '10:20'),
      reason: 'client_visit',
      from: getafeOffice,
      to: CLIENTS[GETAFE][1],
      providerIndex: 2,
      state: 'completed',
      justification: null,
      review: {
        level: 'red',
        decision: 'pending',
        signals: [signal('duplicate', 45), signal('missing_justification', 25)],
        hardRules: [{ code: 'duplicate', text: 'Posible viaje duplicado' }],
      },
    },
    {
      id: `demo-ride-${duplicateDay}-original`,
      rider: alvaro,
      tripAt: madridIso(duplicateDay, '10:15'),
      reason: 'client_visit',
      from: getafeOffice,
      to: CLIENTS[GETAFE][1],
      providerIndex: 2,
      state: 'completed',
      justification: null,
    },
    {
      id: `demo-ride-${weekdayBefore(today, 2)}-toledo`,
      rider: elena,
      tripAt: madridIso(weekdayBefore(today, 2), '21:50'),
      reason: 'late_exit',
      from: madridOffice,
      to: { address: 'Calle de Toledo, 80, Madrid', lat: 40.4085, lng: -3.7105 },
      providerIndex: 1,
      state: 'completed',
      justification: 'Cena con el equipo de la agencia después del lanzamiento.',
      review: { level: 'orange', decision: 'pending', signals: [signal('unrelated_destination', 30), signal('cost_above_average', 15)] },
    },
    {
      id: `demo-ride-${weekdayBefore(today, 5)}-cost`,
      rider: daniel,
      tripAt: madridIso(weekdayBefore(today, 5), '12:35'),
      reason: 'client_visit',
      from: getafeOffice,
      to: CHAMARTIN,
      providerIndex: 1,
      state: 'completed',
      justification: null,
      review: { level: 'orange', decision: 'pending', signals: [signal('cost_above_average', 25), signal('missing_justification', 20)] },
    },
    {
      id: `demo-ride-${weekdayBefore(today, 9)}-approved`,
      rider: martina,
      tripAt: madridIso(weekdayBefore(today, 9), '19:10'),
      reason: 'client_visit',
      from: madridOffice,
      to: IFEMA,
      providerIndex: 0,
      state: 'completed',
      justification: 'Montaje del estand de la feria con el cliente.',
      review: {
        level: 'orange',
        decision: 'approved',
        signals: [signal('unrelated_destination', 30), signal('cost_above_average', 15)],
        decidedAfterMinutes: 16 * 60,
      },
    },
    {
      id: `demo-ride-${weekdayBefore(today, 18)}-approved`,
      rider: hugo,
      tripAt: madridIso(weekdayBefore(today, 18), '06:20'),
      reason: 'other',
      from: hugo.home,
      to: AIRPORT,
      providerIndex: 0,
      state: 'completed',
      justification: 'Vuelo a Lisboa para la auditoría del almacén.',
      review: {
        level: 'orange',
        decision: 'approved',
        signals: [signal('night', 25), signal('cost_above_average', 20)],
        decidedAfterMinutes: 9 * 60,
      },
    },
    {
      id: `demo-ride-${lastSaturday(today, 15)}-rejected`,
      rider: diego,
      tripAt: madridIso(lastSaturday(today, 15), '13:05'),
      reason: 'other',
      from: diego.home,
      to: { address: 'Parque Warner, San Martín de la Vega', lat: 40.2318, lng: -3.5935 },
      providerIndex: 2,
      state: 'completed',
      justification: null,
      review: {
        level: 'red',
        decision: 'rejected',
        signals: [signal('weekend', 30), signal('unrelated_destination', 30), signal('missing_justification', 20)],
        hardRules: [{ code: 'unrelated_destination_without_justification', text: 'Destino no relacionado sin justificación' }],
        decidedAfterMinutes: 2 * 24 * 60,
        rejectReason: 'personal_trip',
        rejectMessage: 'Es un viaje personal: la empresa solo paga los desplazamientos de trabajo.',
      },
    },
  ];
}

/** Importe total (proveedor + comisión) de un trayecto. */
function totalCentsFor(spec: RideSpec, km: number): number {
  if (spec.from === AIRPORT || spec.to === AIRPORT) return AIRPORT_FLAT_CENTS;
  const provider = PROVIDERS[spec.providerIndex];
  const providerCents = Math.round((300 + 115 * km) * provider.factor);
  return Math.round(providerCents * (1 + SAMPLE_RIDE_COMMISSION_PERCENT / 100));
}

/** Nivel automático de un viaje generado: los caros respecto a la media de su sede quedan en «baja sospecha». */
function autoReview(totalCents: number): ReviewSpec {
  return totalCents > 3500
    ? { level: 'yellow', decision: 'auto_approved', signals: [signal('cost_above_average', 15)] }
    : { level: 'green', decision: 'auto_approved', signals: [] };
}

function toRide(spec: RideSpec): CorporateRideDetailApi & { _review: ReviewSpec | null } {
  const provider = PROVIDERS[spec.providerIndex];
  const km = roadKm(spec.from, spec.to);
  const total = totalCentsFor(spec, km);
  const providerCents = Math.round(total / (1 + SAMPLE_RIDE_COMMISSION_PERCENT / 100));
  const commission = total - providerCents;
  const office = OFFICES[spec.rider.officeId];
  const completed = spec.state === 'completed';
  const cancelled = spec.state === 'cancelled_by_rider';
  const awaiting = spec.state === 'in_progress' || !!spec.awaitingReceipt;
  const overLimit = completed && total > SAMPLE_RIDE_LIMIT_CENTS;
  const companyShare = completed ? Math.min(total, SAMPLE_RIDE_LIMIT_CENTS) : null;
  const riderShare = completed ? total - companyShare! : null;
  const fee = spec.cancelFeeCents ?? 0;
  const companyAmount = cancelled ? fee : awaiting ? 0 : companyShare!;
  const stateGroup: CorporateRideStateGroup = completed ? 'completed' : cancelled ? 'cancelled' : 'in_progress';
  const minutes = Math.round(km * 2.2 + 6);
  const endedAt = completed ? plusMinutes(spec.tripAt, minutes) : cancelled ? plusMinutes(spec.tripAt, 3) : null;
  const review = completed && !awaiting ? spec.review ?? autoReview(total) : null;
  const decided = review && review.decision !== 'pending' && review.decision !== 'auto_approved' ? review : null;
  const decidedAt = decided ? plusMinutes(endedAt!, decided.decidedAfterMinutes ?? 60) : null;
  const amounts = { provider_amount_cents: providerCents, commission_amount_cents: commission, total_amount_cents: total };
  return {
    id: spec.id,
    trip_at: spec.tripAt,
    created_at: plusMinutes(spec.tripAt, -4),
    scheduled_at: null,
    ended_at: endedAt,
    state: spec.state,
    state_group: stateGroup,
    trip_reason: spec.reason,
    employee: {
      user_id: `demo-app-user-${spec.rider.n}`,
      name: spec.rider.name,
      surname: spec.rider.surname,
      email: `${slug(spec.rider.name)}.${spec.rider.n}@ejemplo.es`,
      department: spec.rider.department,
    },
    office: { id: spec.rider.officeId, name: office.name },
    pickup_address: spec.from.address,
    dropoff_address: spec.to.address,
    provider: provider.provider,
    provider_display_name: provider.display,
    product_display_name: provider.product,
    currency: 'EUR',
    company_amount_cents: companyAmount,
    company_amount_status: awaiting ? 'awaiting_receipt' : 'charged',
    employee_amount_cents: riderShare ? riderShare : null,
    review_level: review?.level ?? null,
    review_decision: review?.decision ?? null,
    accepted_at: cancelled ? null : plusMinutes(spec.tripAt, -3),
    pickup_latitude: spec.from.lat,
    pickup_longitude: spec.from.lng,
    dropoff_latitude: spec.to.lat,
    dropoff_longitude: spec.to.lng,
    polyline: null,
    split: {
      corporate_limit_cents: SAMPLE_RIDE_LIMIT_CENTS,
      corporate_share_cents: awaiting ? null : companyShare,
      rider_share_cents: awaiting ? null : riderShare,
      over_limit: completed ? overLimit : null,
    },
    commission: {
      percent: SAMPLE_RIDE_COMMISSION_PERCENT,
      estimated: amounts,
      final: completed && !awaiting ? amounts : null,
    },
    cancellation: cancelled ? { penalty_cents: fee, company_cents: fee, employee_cents: null } : null,
    has_receipt: completed && !awaiting,
    trip_justification: spec.justification ?? null,
    reclaim:
      decided?.decision === 'rejected'
        ? {
            status: 'reclaimed',
            amount_cents: companyAmount,
            charged_cents: companyAmount,
            reason: decided.rejectReason ?? null,
            decided_at: decidedAt!,
            requested_at: decidedAt,
            resolved_at: plusMinutes(decidedAt!, 30),
          }
        : null,
    _review: review,
  };
}

function slug(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();
}

type SampleRide = ReturnType<typeof toRide>;

/** Todas las filas de «Viajes y gasto», de la más reciente a la más antigua (con el detalle de cada una). */
function allRides(now: Date): SampleRide[] {
  const today = madridDay(now);
  const specs: RideSpec[] = [];
  for (let daysAgo = 1; daysAgo < SAMPLE_RIDE_HISTORY_DAYS; daysAgo++) {
    specs.push(...generatedRides(addDays(today, -daysAgo), daysAgo));
  }
  // Los dos últimos viajes de ayer (o del último laborable) aún esperan el recibo del proveedor.
  const latest = specs.filter((s) => s.state === 'completed').sort((a, b) => b.tripAt.localeCompare(a.tripAt));
  for (const s of latest.slice(0, 2)) s.awaitingReceipt = true;
  specs.push(...curatedRides(today));
  // Uno en curso ahora mismo: Lucía vuelve de visitar a un cliente.
  specs.push({
    id: `demo-ride-${today}-live`,
    rider: riderByN(1),
    tripAt: madridIsoOf(new Date(now.getTime() - 12 * 60_000)),
    reason: 'client_visit',
    from: CLIENTS[MADRID][2],
    to: OFFICES[MADRID],
    providerIndex: 1,
    state: 'in_progress',
    justification: null,
  });
  return specs.map(toRide).sort((a, b) => b.trip_at.localeCompare(a.trip_at) || b.id.localeCompare(a.id));
}

/** Fila del listado sin lo que solo lleva el detalle. */
function listRow(ride: SampleRide): CorporateRideDetailApi {
  const { _review, ...row } = ride;
  return row;
}

/** Filas de «Viajes y gasto» (con su detalle), de la más reciente a la más antigua. */
export function sampleCorporateRides(now: Date): CorporateRideDetailApi[] {
  return allRides(now).map(listRow);
}

export interface SampleRideFilters {
  from?: string | null;
  to?: string | null;
  officeId?: string | null;
  stateGroup?: string | null;
  reviewDecision?: string | null;
  reviewLevel?: string | null;
  q?: string | null;
}

/** Los filtros del listado, como el back: días de Madrid, sede, estado, revisión y búsqueda por persona o dirección. */
export function filterSampleRides(rows: CorporateRideDetailApi[], f: SampleRideFilters): CorporateRideDetailApi[] {
  const q = f.q?.trim().toLowerCase();
  return rows.filter((r) => {
    const day = r.trip_at.slice(0, 10);
    if (f.from && day < f.from) return false;
    if (f.to && day > f.to) return false;
    if (f.officeId && r.office?.id !== f.officeId) return false;
    if (f.stateGroup && r.state_group !== f.stateGroup) return false;
    if (f.reviewDecision && r.review_decision !== f.reviewDecision) return false;
    if (f.reviewLevel && r.review_level !== f.reviewLevel) return false;
    if (q) {
      const haystack = [r.employee.name, r.employee.surname, r.employee.email, r.pickup_address, r.dropoff_address];
      if (!haystack.some((v) => v?.toLowerCase().includes(q))) return false;
    }
    return true;
  });
}

/** Rango de días del periodo y el anterior, como `CorporationRidePeriodResolver`. */
export function sampleRidePeriodRanges(period: CorporateRidePeriod, now: Date): { current: { from: string; to: string }; previous: { from: string; to: string } } {
  const today = madridDay(now);
  if (period === 'this_year') {
    const year = Number(today.slice(0, 4));
    return {
      current: { from: `${year}-01-01`, to: today },
      previous: { from: `${year - 1}-01-01`, to: `${year - 1}${today.slice(4)}` },
    };
  }
  const days = period === 'last_90_days' ? 90 : 30;
  const from = addDays(today, -(days - 1));
  return { current: { from, to: today }, previous: { from: addDays(from, -days), to: addDays(from, -1) } };
}

function delta(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null || previous === 0) return null;
  return Math.round(((current - previous) / previous) * 1000) / 10;
}

/** Lo que agrega el back de los viajes terminados de un rango (`CorporationRideKpiService::aggregate`). */
function aggregate(rows: CorporateRideDetailApi[], from: string, to: string) {
  const terminal = rows.filter((r) => r.state_group !== 'in_progress' && r.trip_at.slice(0, 10) >= from && r.trip_at.slice(0, 10) <= to);
  const completed = terminal.filter((r) => r.state_group === 'completed');
  const withReceipt = completed.filter((r) => r.company_amount_status !== 'awaiting_receipt');
  return {
    spend: sum(terminal.map((r) => r.company_amount_cents)),
    completed: completed.length,
    awaitingReceipt: completed.length - withReceipt.length,
    average: withReceipt.length ? Math.round(sum(withReceipt.map((r) => r.company_amount_cents)) / withReceipt.length) : null,
    employees: new Set(completed.map((r) => r.employee.user_id)).size,
  };
}

/**
 * KPIs de «Viajes y gasto» del periodo y la sede, con las reglas del back:
 * solo cuentan los viajes terminados, la media va sin los que esperan recibo,
 * y las 8 semanas ISO acaban en la actual. Las personas que pueden pedir
 * viajes son las vinculadas de la plantilla de ejemplo (`sampleMetrics`).
 */
export function sampleRideKpis(period: CorporateRidePeriod, officeId: string | null, now: Date): CorporateRideKpisApi {
  const rows = filterSampleRides(sampleCorporateRides(now), { officeId });
  const ranges = sampleRidePeriodRanges(period, now);
  const current = aggregate(rows, ranges.current.from, ranges.current.to);
  const previous = aggregate(rows, ranges.previous.from, ranges.previous.to);
  const today = madridDay(now);
  const monday = addDays(today, -((weekday(today) + 6) % 7));
  const weeks = Array.from({ length: 8 }, (_, i) => {
    const start = addDays(monday, -7 * (7 - i));
    const end = addDays(start, 6);
    return { week_start: start, week_end: end, amount_cents: aggregate(rows, start, end).spend };
  });
  return {
    corporation_id: CORPORATION_ID,
    corporation_office_id: officeId,
    period,
    range: ranges.current,
    previous_range: ranges.previous,
    currency: 'EUR',
    company_spend: { amount_cents: current.spend, previous_amount_cents: previous.spend, delta_percent: delta(current.spend, previous.spend) },
    completed_rides: { count: current.completed, previous_count: previous.completed, delta_percent: delta(current.completed, previous.completed) },
    average_company_amount: {
      amount_cents: current.average,
      previous_amount_cents: previous.average,
      delta_percent: delta(current.average, previous.average),
    },
    employees_with_rides: {
      count: current.employees,
      previous_count: previous.employees,
      delta_percent: delta(current.employees, previous.employees),
      eligible_employees: sampleMetrics({ officeId }).linked,
    },
    rides_awaiting_receipt: { count: current.awaitingReceipt },
    weekly_company_spend: weeks,
  };
}

// ─── Revisión ────────────────────────────────────────────────────────────────

/** Viajes revisados (los terminados con importe), los mismos de «Viajes y gasto», con su revisión. */
export function sampleRideReviews(now: Date): RideReviewDetailApi[] {
  return allRides(now)
    .filter((r) => r._review)
    .map((r) => {
      const review = r._review!;
      const decided = review.decision === 'approved' || review.decision === 'rejected';
      const scoredAt = plusMinutes(r.ended_at!, 2);
      const decidedAt = decided ? plusMinutes(r.ended_at!, review.decidedAfterMinutes ?? 60) : review.decision === 'auto_approved' ? scoredAt : null;
      return {
        ride_id: r.id,
        level: review.level,
        score: sum(review.signals.map((s) => s.points)),
        decision: review.decision,
        scored_at: scoredAt,
        decided_at: decidedAt,
        trip_at: r.trip_at,
        employee: { user_id: r.employee.user_id, name: r.employee.name, surname: r.employee.surname, department: r.employee.department },
        office: r.office,
        pickup_address: r.pickup_address,
        dropoff_address: r.dropoff_address,
        provider: r.provider_display_name,
        company_amount_cents: r.company_amount_cents,
        employee_amount_cents: r.employee_amount_cents,
        trip_reason: r.trip_reason,
        trip_justification: r.trip_justification,
        signals: review.signals,
        product: r.product_display_name,
        pickup: { lat: r.pickup_latitude!, lng: r.pickup_longitude! },
        dropoff: { lat: r.dropoff_latitude!, lng: r.dropoff_longitude! },
        hard_rules: review.hardRules ?? [],
        decided_by: decided ? { user_id: `demo-app-user-${REVIEWER.n}`, name: REVIEWER.name, surname: REVIEWER.surname } : null,
        reject_reason_code: review.rejectReason ?? null,
        reject_reason_text: null,
        reject_message: review.rejectMessage ?? null,
      };
    });
}

/** La bandeja: pendientes, aprobados solos o resueltos por la empresa, y el nivel. */
export function filterSampleRideReviews(rows: RideReviewDetailApi[], status: string | null, level: string | null): RideReviewDetailApi[] {
  return rows.filter((r) => {
    if (status === 'pending' && r.decision !== 'pending') return false;
    if (status === 'auto_approved' && r.decision !== 'auto_approved') return false;
    if (status === 'resolved' && r.decision !== 'approved' && r.decision !== 'rejected') return false;
    if (level && r.level !== level) return false;
    return true;
  });
}

/** KPIs de la bandeja: lo analizado en 30 días y el contador de cada filtro (el total de su listado). */
export function sampleRideReviewKpis(now: Date): RideReviewKpisApi {
  const rows = sampleRideReviews(now);
  const to = madridDay(now);
  const from = addDays(to, -29);
  const window = rows.filter((r) => r.trip_at.slice(0, 10) >= from && r.trip_at.slice(0, 10) <= to);
  const auto = window.filter((r) => r.decision === 'auto_approved');
  const count = (status: string, level: string | null = null) => filterSampleRideReviews(rows, status, level).length;
  return {
    from,
    to,
    analyzed: window.length,
    auto_approved: auto.length,
    auto_approved_low_suspicion: auto.filter((r) => r.level === 'yellow').length,
    counts: {
      pending: count('pending'),
      pending_red: count('pending', 'red'),
      pending_orange: count('pending', 'orange'),
      low_suspicion: count('auto_approved', 'yellow'),
      resolved: count('resolved'),
    },
  };
}

// ─── Reservas de grupo ───────────────────────────────────────────────────────

/** Reglas de las reservas (las de Configuración › Rides). */
export const SAMPLE_RIDE_BOOKING_RULES: RideBookingRulesApi = {
  min_lead_hours: 2,
  max_lead_days: 30,
  free_cancellation_hours: 24,
  late_cancellation_fee_percent: 50,
  vehicle_capacities: { economy: 4, executive: 3, van: 6 },
  commission_percent: SAMPLE_RIDE_COMMISSION_PERCENT,
};

interface BookingSpec {
  n: number;
  status: RideBookingStatus;
  concept: string;
  officeId: string;
  costCenter: string;
  from: Place;
  to: Place;
  /** Días desde hoy y hora de Madrid; o minutos desde ahora (la que va en curso). */
  pickup: { days: number; time: string } | { minutes: number };
  passengers: number[];
  category: RideBookingCategory;
  providerIndex: number;
  /** Importe del proveedor de cada vehículo. */
  vehicleCents: number[];
  notes?: string;
}

function bookingSpecs(): BookingSpec[] {
  const m = OFFICES[MADRID];
  const g = OFFICES[GETAFE];
  return [
    // Próximas (las de los marcadores de Inicio).
    { n: 1, status: 'requesting', concept: 'Clientes de Lisboa al aeropuerto', officeId: MADRID, costCenter: 'VENTAS', from: m, to: AIRPORT, pickup: { days: 1, time: '17:00' }, passengers: [3, 12], category: 'executive', providerIndex: 0, vehicleCents: [3000] },
    { n: 2, status: 'scheduled', concept: 'Cena de equipo de Ventas', officeId: MADRID, costCenter: 'VENTAS', from: m, to: { address: 'Calle de Jorge Juan, 35, Madrid', lat: 40.4236, lng: -3.6838 }, pickup: { days: 2, time: '20:30' }, passengers: [3, 5, 7, 12, 13, 14, 15, 16, 17], category: 'economy', providerIndex: 0, vehicleCents: [1450, 1450, 1450], notes: 'Recogida en la puerta principal.' },
    { n: 3, status: 'scheduled', concept: 'Feria de logística en IFEMA', officeId: GETAFE, costCenter: 'LOGISTICA', from: g, to: IFEMA, pickup: { days: 5, time: '08:15' }, passengers: [2, 6, 8, 10, 21], category: 'van', providerIndex: 1, vehicleCents: [4200] },
    { n: 4, status: 'scheduled', concept: 'Formación de seguridad en el centro logístico', officeId: MADRID, costCenter: 'RRHH', from: m, to: g, pickup: { days: 8, time: '08:30' }, passengers: [1, 5, 11, 14, 15, 16, 17], category: 'economy', providerIndex: 2, vehicleCents: [2350, 2350] },
    { n: 5, status: 'in_progress', concept: 'Auditores desde Atocha', officeId: GETAFE, costCenter: 'ADMIN', from: ATOCHA, to: g, pickup: { minutes: -25 }, passengers: [2], category: 'economy', providerIndex: 1, vehicleCents: [2600] },
    // Pasadas.
    { n: 6, status: 'completed', concept: 'Comida con clientes de Ventas', officeId: MADRID, costCenter: 'VENTAS', from: m, to: { address: 'Calle de Jorge Juan, 22, Madrid', lat: 40.4232, lng: -3.6862 }, pickup: { days: -6, time: '13:45' }, passengers: [3, 7, 12, 13, 15], category: 'economy', providerIndex: 0, vehicleCents: [1380, 1420] },
    { n: 7, status: 'cancelled', concept: 'Visita a la planta de Getafe', officeId: MADRID, costCenter: 'DIRECCION', from: m, to: g, pickup: { days: -9, time: '09:00' }, passengers: [11, 12, 16], category: 'economy', providerIndex: 2, vehicleCents: [2300] },
    { n: 8, status: 'completed', concept: 'Traslado a la estación de Atocha', officeId: GETAFE, costCenter: 'VENTAS', from: g, to: ATOCHA, pickup: { days: -13, time: '07:40' }, passengers: [8, 10, 21], category: 'economy', providerIndex: 2, vehicleCents: [2150] },
    { n: 9, status: 'completed', concept: 'Jornada de equipo en Madrid Río', officeId: MADRID, costCenter: 'RRHH', from: m, to: { address: 'Paseo de la Chopera, 14, Madrid', lat: 40.3947, lng: -3.7003 }, pickup: { days: -24, time: '09:15' }, passengers: [1, 3, 5, 7, 11, 13, 14, 15, 16, 17], category: 'economy', providerIndex: 1, vehicleCents: [1690, 1720, 1650] },
    { n: 10, status: 'completed', concept: 'Visita de proveedores al almacén', officeId: GETAFE, costCenter: 'LOGISTICA', from: CHAMARTIN, to: g, pickup: { days: -41, time: '10:00' }, passengers: [6, 21], category: 'economy', providerIndex: 0, vehicleCents: [3100] },
  ];
}

const VEHICLE_RIDE_STATE: Partial<Record<RideBookingStatus, string>> = {
  scheduled: 'scheduled',
  in_progress: 'in_progress',
  completed: 'completed',
  cancelled: 'cancelled_by_rider',
};

const DRIVERS = ['Jorge M.', 'Rosa P.', 'Iñaki L.', 'Fátima B.', 'Óscar R.'];
const CARS = [
  { plate: '4821 KLM', make: 'Toyota', model: 'Prius' },
  { plate: '7310 LBC', make: 'Kia', model: 'Niro' },
  { plate: '2954 MDF', make: 'Hyundai', model: 'Ioniq' },
  { plate: '6107 LTR', make: 'Mercedes', model: 'Vito' },
];

function toBooking(spec: BookingSpec, now: Date): RideBookingApi {
  const today = madridDay(now);
  const provider = PROVIDERS[spec.providerIndex];
  const pickupAt = 'minutes' in spec.pickup ? madridIsoOf(new Date(now.getTime() + spec.pickup.minutes * 60_000)) : madridIso(addDays(today, spec.pickup.days), spec.pickup.time);
  // Pedida cuatro días antes de la recogida y, si eso aún no ha llegado, días antes de hoy.
  const createdAt = madridIsoOf(new Date(Math.min(Date.parse(pickupAt) - 4 * 86_400_000, now.getTime() - (spec.n + 1) * 20 * 3_600_000)));
  const vehicles = spec.vehicleCents.length;
  const subtotal = sum(spec.vehicleCents);
  const commission = Math.round((subtotal * SAMPLE_RIDE_COMMISSION_PERCENT) / 100);
  const estimateMax = Math.round(subtotal * 1.2);
  const done = spec.status === 'completed';
  const cancelled = spec.status === 'cancelled';
  const live = spec.status !== 'requesting' && !cancelled;
  const rideState = VEHICLE_RIDE_STATE[spec.status] ?? null;
  // Cada vehículo lleva a un titular y a los que caben con él.
  const capacity = SAMPLE_RIDE_BOOKING_RULES.vehicle_capacities[spec.category];
  const groups = Array.from({ length: vehicles }, (_, i) => spec.passengers.slice(i * capacity, (i + 1) * capacity));
  const vehicleRows: RideBookingVehicleDetailApi[] = groups.map((group, i) => {
    const rideId = live || cancelled ? `demo-booking-${spec.n}-ride-${i + 1}` : null;
    const car = CARS[(spec.n + i) % CARS.length];
    return {
      id: `demo-booking-${spec.n}-vehicle-${i + 1}`,
      position: i + 1,
      status: spec.status === 'requesting' ? 'requesting' : cancelled ? 'cancelled' : 'booked',
      attempts: 1,
      failure_reason: null,
      ride_id: rideId,
      provider: provider.provider,
      category: spec.category,
      driver_name: null,
      plate: null,
      final_amount_cents: done ? spec.vehicleCents[i] : null,
      ride: rideId
        ? {
            id: rideId,
            state: rideState,
            provider_display_name: provider.display,
            product_display_name: provider.product,
            amount_cents: spec.vehicleCents[i],
            currency: 'EUR',
            titular_user_id: `demo-user-${group[0]}`,
            driver: spec.status === 'scheduled' ? null : { name: DRIVERS[(spec.n + i) % DRIVERS.length] },
            vehicle: spec.status === 'scheduled' ? null : { license_plate: car.plate, make: car.make, model: car.model },
          }
        : null,
    };
  });
  const passengers = groups.flatMap((group, i) =>
    group.map((n, j) => ({ corporation_user_id: `demo-user-${n}`, ride_id: vehicleRows[i].ride_id ?? null, titular: j === 0 })),
  );
  const startedAt = spec.status === 'in_progress' || done ? plusMinutes(pickupAt, 2) : null;
  const completedAt = done ? plusMinutes(pickupAt, 35) : null;
  const settledAt = done ? plusMinutes(completedAt!, 20) : null;
  const cancelledAt = cancelled ? plusMinutes(pickupAt, -2 * 24 * 60) : null;
  const events = [
    { type: 'requested', at: createdAt, actor_user_id: `demo-app-user-${REVIEWER.n}`, data: { estimate_max_cents: estimateMax } },
    ...(spec.status === 'requesting' ? [] : [{ type: 'hold_succeeded', at: plusMinutes(createdAt, 1), actor_user_id: null, data: null }]),
    ...(spec.status === 'requesting' ? [] : vehicleRows.map((v) => ({ type: 'vehicle_booked', at: plusMinutes(createdAt, 2), actor_user_id: null, data: { position: v.position } }))),
    ...(cancelled ? [{ type: 'cancelled', at: cancelledAt!, actor_user_id: `demo-app-user-${REVIEWER.n}`, data: { fee_cents: 0 } }] : []),
    ...(startedAt ? [{ type: 'ride_started', at: startedAt, actor_user_id: null, data: { position: 1 } }] : []),
    ...(done
      ? [
          ...vehicleRows.map((v) => ({ type: 'ride_completed', at: completedAt!, actor_user_id: null, data: { position: v.position, amount_cents: v.final_amount_cents } })),
          { type: 'settled', at: settledAt!, actor_user_id: null, data: { charged_cents: subtotal + commission } },
        ]
      : []),
  ];
  return {
    id: `demo-booking-${spec.n}`,
    corporation_id: CORPORATION_ID,
    status: spec.status,
    hold_status: spec.status === 'requesting' ? 'pending' : 'held',
    concept: spec.concept,
    cost_center: spec.costCenter,
    office: { id: spec.officeId, name: OFFICES[spec.officeId].name },
    notes: spec.notes ?? null,
    origin: { address: spec.from.address, latitude: spec.from.lat, longitude: spec.from.lng },
    destination: { address: spec.to.address, latitude: spec.to.lat, longitude: spec.to.lng },
    pickup_at: pickupAt,
    passengers_count: spec.passengers.length,
    vehicles_count: vehicles,
    category: spec.category,
    service: 'vtc',
    ride_group_id: SAMPLE_RIDE_GROUP_ID,
    product: { provider: provider.provider, product_id: `demo-${provider.provider}-${spec.category}`, name: provider.product, display_name: `${provider.display} ${provider.product}` },
    total_cents: cancelled ? 0 : subtotal,
    estimate_min_cents: subtotal,
    estimate_max_cents: estimateMax,
    commission_percent: SAMPLE_RIDE_COMMISSION_PERCENT,
    currency: 'EUR',
    held_cents: spec.status === 'requesting' ? null : estimateMax,
    cancelled_by: cancelled ? 'company' : null,
    cancel_reason: cancelled ? 'company_request' : null,
    cancel_note: null,
    cancel_fee_cents: cancelled ? 0 : null,
    final_amount_cents: done ? subtotal : null,
    commission_cents: done ? commission : null,
    charged_cents: done ? subtotal + commission : null,
    settled_at: settledAt,
    charge_failed: false,
    created_by_user_id: `demo-app-user-${REVIEWER.n}`,
    created_at: createdAt,
    confirmed_at: spec.status === 'requesting' ? null : plusMinutes(createdAt, 2),
    started_at: startedAt,
    completed_at: completedAt,
    cancelled_at: cancelledAt,
    passengers,
    vehicles: vehicleRows,
    events,
  };
}

/** Reservas de grupo de «WAIIS S.L.»: cinco próximas o en curso y cinco pasadas, de la más reciente a la más antigua. */
export function sampleRideBookings(now: Date): RideBookingApi[] {
  return bookingSpecs()
    .map((s) => toBooking(s, now))
    .sort((a, b) => b.pickup_at.localeCompare(a.pickup_at));
}

export interface SampleRideBookingFilters {
  status?: string | null;
  from?: string | null;
  to?: string | null;
  officeId?: string | null;
  sort?: string | null;
}

/** Los filtros del listado de reservas: estado, días de recogida (Madrid), sede y orden. */
export function filterSampleRideBookings(rows: RideBookingApi[], f: SampleRideBookingFilters): RideBookingApi[] {
  const kept = rows.filter((b) => {
    const day = b.pickup_at.slice(0, 10);
    if (f.status && b.status !== f.status) return false;
    if (f.from && day < f.from) return false;
    if (f.to && day > f.to) return false;
    if (f.officeId && b.office?.id !== f.officeId) return false;
    return true;
  });
  return f.sort === 'pickup_asc' ? [...kept].sort((a, b) => a.pickup_at.localeCompare(b.pickup_at)) : kept;
}

/** Justificante de una reserva completada; `null` si no lo tiene (la pantalla dice que no se ha podido cargar). */
export function sampleRideBookingReceipt(booking: RideBookingApi): RideBookingReceiptApi | null {
  if (booking.status !== 'completed') return null;
  return {
    booking_id: booking.id,
    corporation: { id: CORPORATION_ID, trade_name: 'WAIIS S.L.' },
    concept: booking.concept,
    cost_center: booking.cost_center,
    pickup_at: booking.pickup_at,
    origin: { address: booking.origin.address },
    destination: { address: booking.destination.address },
    passengers_count: booking.passengers_count,
    vehicles_count: booking.vehicles_count,
    category: booking.category,
    service: booking.service,
    vehicles: (booking.vehicles ?? []).map((v) => ({
      position: v.position,
      provider: v.provider!,
      category: v.category!,
      driver_name: v.ride?.driver?.name ?? null,
      plate: v.ride?.vehicle?.license_plate ?? null,
      final_amount_cents: v.final_amount_cents ?? null,
    })),
    subtotal_cents: booking.final_amount_cents,
    commission_percent: booking.commission_percent,
    commission_cents: booking.commission_cents,
    total_cents: booking.charged_cents,
    currency: 'EUR',
    payment_status: 'paid',
    charged_cents: booking.charged_cents,
    completed_at: booking.completed_at,
    settled_at: booking.settled_at,
  };
}

// ─── Grupos de la política (los lee el detalle de una reserva) ──────────────

export const SAMPLE_RIDE_GROUP_ID = 'demo-ride-group-default';

/** El grupo predeterminado: la empresa paga hasta el tope por viaje, cualquier servicio, en horario laboral ampliado. */
export function sampleRideGroups(): RideGroupApi[] {
  return [
    {
      id: SAMPLE_RIDE_GROUP_ID,
      name: 'Todos los empleados',
      is_default: true,
      company_pays: true,
      per_trip_limit_cents: SAMPLE_RIDE_LIMIT_CENTS,
      service: 'any',
      max_category: 'economy',
      schedule_enabled: false,
      schedule_from: null,
      schedule_to: null,
      schedule_days: [1, 2, 3, 4, 5],
      members_count: sampleMetrics().linked,
    },
  ];
}

/** Empleados que van en las reservas, con su sede: los pasajeros salen con nombre. */
export function sampleRideGroupEmployees(): RideGroupEmployeeApi[] {
  return Object.entries(BOOKING_PASSENGERS).flatMap(([officeId, ns]) =>
    ns.map((n) => {
      const rider = SAMPLE_TAXI_RIDERS.find((r) => r.n === n);
      const person = rider ?? EXTRA_PASSENGERS[n];
      return {
        corporation_user_id: `demo-user-${n}`,
        user_id: `demo-app-user-${n}`,
        name: person.name,
        surname: person.surname,
        email: `${slug(person.name)}.${n}@ejemplo.es`,
        department: person.department,
        office: { id: officeId, name: OFFICES[officeId].name },
        ride_group: { id: SAMPLE_RIDE_GROUP_ID, name: 'Todos los empleados', is_default: true },
      };
    }),
  );
}

/** Pasajeros de las reservas que no piden viajes sueltos (los de `employees.json`). */
const EXTRA_PASSENGERS: Record<number, { name: string; surname: string; department: string }> = {
  14: { name: 'Mario', surname: 'Rubio Marín', department: 'Logística' },
  15: { name: 'Lola', surname: 'Sanz Iglesias', department: 'Ventas' },
  17: { name: 'Irene', surname: 'Castillo Santos', department: 'Atención al cliente' },
};
